import { useTracking } from '@eventbrite/ads';
import { SimpleCarousel } from '@eventbrite/collection-carousel';
import {
    EventCardShareAction,
    FormattedEvent,
} from '@eventbrite/event-renderer';
import { GenericLazyString } from '@eventbrite/i18n';
import loadable from '@loadable/component';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Waypoint } from 'react-waypoint';
import { OrganicEventSearchFiltersApiPayload } from '../../../../api/searchOrganicEvents';
import DiscoverVerticalEventCard from '../../../../components/DiscoverEventCard/Vertical/DiscoverVerticalBrowseEventCard';
import { DiscoverState, Location, ShareOptions } from '../../../../types/index';
import { CategoryEventsHeader } from '../../components/CategoryEventsHeader';
import { CategoryEventsLoadingState } from '../../components/CategoryEventsLoadingState';
import { HEAP_LOCATION_STRING, UtmOptions } from '../../constants/analytics';
import { useCategoryEventsQuery } from '../../hooks/events/events';
import { usePromotedEvent } from '../../hooks/events/promotedEvents';
import { updateCarouselCategoryState } from '../../redux/content';
import { aggregateContext } from '../../utils/analytics';
import { CategoryEventsViewMore } from '../CategoryEventsViewMore';
import './CategoryEventsContainer.scss';

const CategoryPromotedEvents = loadable(
    () => import('./CategoryPromotedEvents'),
);
const EventCardSlide: React.FunctionComponent = (props) => {
    return (
        <div className="event-card-simple-carousel-slide">{props.children}</div>
    );
};

interface CategoryEventsProps {
    queryOverload?: OrganicEventSearchFiltersApiPayload;
    categoryKey?: string;
    categoryTitle?: string | GenericLazyString;
    viewMore?: string | GenericLazyString;
    categoryIcon?: JSX.Element;
    locale?: string;
    isMobile?: boolean;
    location: Location;
    dataTestId?: string;
    updateCarouselCategoryState: Function;
    bucketPosition: number;
    userId?: string;
    guestId?: string;
    shareOptions: ShareOptions;
}

const CategoryEventsContainer = ({
    queryOverload,
    categoryKey,
    categoryTitle,
    viewMore,
    categoryIcon,
    locale,
    isMobile,
    location,
    dataTestId,
    updateCarouselCategoryState,
    bucketPosition,
    userId,
    guestId,
    shareOptions,
}: CategoryEventsProps) => {
    const [hasEnteredViewport, setHasEnteredViewport] = useState(false);
    const { data, isLoading } = useCategoryEventsQuery({
        queryOverload: queryOverload,
        refetchOnMount: !queryOverload,
        location: location,
        enabled: location?.currentPlace ? true : false,
    });
    const events = data?.events || [];
    const currentBucketHasContent = events && events.length >= 1;

    const { promotedEvents = [], arePromotedEventsLoading } = usePromotedEvent({
        search: queryOverload,
        enabled: !isLoading && currentBucketHasContent && hasEnteredViewport,
        location: location,
    });
    const promotedEvent = promotedEvents?.[0];

    const utmOptions: UtmOptions = {
        'utm-campaign': 'social',
        'utm-content': 'attendeeshare',
        'utm-medium': 'discovery',
        'utm-term': 'listing',
        'utm-source': 'cp',
    };
    const { ref } = useTracking<HTMLDivElement>(({ id, event }) =>
        aggregateContext({
            id,
            event,
            bucketKey: categoryKey,
            bucketPosition: bucketPosition.toString(),
            events: [...promotedEvents, ...events],
            eventSearch: data?.eventSearch,
            userId: userId,
            locale,
            guestId: guestId,
        }),
    );

    useEffect(() => {
        if (isLoading || (data && data.events && data.events.length > 0)) {
            updateCarouselCategoryState(bucketPosition, true);
        } else if (data && data.events && data.events.length == 0) {
            updateCarouselCategoryState(bucketPosition, false);
        }
    }, [bucketPosition, data, isLoading, updateCarouselCategoryState]);

    useEffect(() => {
        if (location && location.currentPlace) {
            setHasEnteredViewport(false);
        }
    }, [location]);

    //Loading state
    if (isLoading) {
        return <CategoryEventsLoadingState />;
    }

    if (!(events && events?.length > 0)) {
        return null;
    }

    // Return event category bucket
    return (
        <div
            data-event-bucket-label={categoryKey?.toString()}
            className="category-events-container"
            data-testid={dataTestId}
            ref={ref}
        >
            <Waypoint
                scrollableAncestor={'window'}
                bottomOffset={'-10px'}
                onEnter={() => {
                    setHasEnteredViewport(true);
                }}
            >
                <div>
                    <div className="category-event-bucket">
                        <SimpleCarousel
                            slidesVisible={{ desktop: 3, tablet: 2 }}
                            headerTitle={
                                <CategoryEventsHeader
                                    title={categoryTitle}
                                    icon={categoryIcon}
                                >
                                    {!isMobile &&
                                        events &&
                                        events?.length > 4 && (
                                            <CategoryEventsViewMore
                                                category={categoryKey}
                                            >
                                                {viewMore}
                                            </CategoryEventsViewMore>
                                        )}
                                </CategoryEventsHeader>
                            }
                            lazilyRenderOffScreenElements={true}
                        >
                            {(promotedEvent || arePromotedEventsLoading) && (
                                <EventCardSlide>
                                    <CategoryPromotedEvents
                                        isLoading={arePromotedEventsLoading}
                                        event={promotedEvent}
                                        utmOptions={utmOptions}
                                        data-testid="homepage-promoted-event"
                                    />
                                </EventCardSlide>
                            )}
                            {events?.map((event) => {
                                return (
                                    <EventCardSlide key={event.id}>
                                        <DiscoverVerticalEventCard
                                            locale={locale || 'en_US'}
                                            statsigLocationString={
                                                HEAP_LOCATION_STRING
                                            }
                                            isAuthenticated={false}
                                            event={event as FormattedEvent}
                                            isLazyImage
                                            affCode={'ehometext'}
                                            data-testid="event-category-carousel-event"
                                            moreActions={
                                                <EventCardShareAction
                                                    shareOptions={{
                                                        ...shareOptions,
                                                        eventId: event.id,
                                                        eventName: event.name,
                                                        eventUrl: event.url,
                                                        utmOptions,
                                                        trackingCategory:
                                                            'eventcard',
                                                    }}
                                                />
                                            }
                                        />
                                    </EventCardSlide>
                                );
                            })}
                        </SimpleCarousel>
                        {isMobile && events && events?.length > 4 && (
                            <CategoryEventsViewMore category={categoryKey}>
                                {viewMore}
                            </CategoryEventsViewMore>
                        )}
                    </div>
                    {events && events.length > 0 && (
                        <div className="horizontalBar"></div>
                    )}
                </div>
            </Waypoint>
        </div>
    );
};

const _mapStateToProps = ({
    app,
    location,
    user,
    shareOptions,
}: DiscoverState) => {
    return {
        location: location,
        locale: app.locale,
        isMobile: app.isMobile,
        userId: user.publicId,
        guestId: user.guestId,
        shareOptions: shareOptions,
    };
};

const _mapDispatchToProps = {
    updateCarouselCategoryState,
};

export default connect(
    _mapStateToProps,
    _mapDispatchToProps,
)(CategoryEventsContainer);
